import { Loader } from "@circle-react-shared/uikit/Loader";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useChatParticipantsApi } from "@circle-react/hooks/chatsV2/useChatParticipantsApi";
import { useOpenMemberProfile } from "@circle-react/hooks/profile/useOpenMemberProfile";
import InfiniteScroll from "react-infinite-scroll-component";

import { t } from "@/i18n-js/instance";
import { BlockDmsModal } from "@/react/components/Chat/sharedV2/BlockDmsModal";
import { useBlockMember } from "@/react/components/Spaces/ChatSpace/RailBar/hooks/useBlockMember";
import { ParticipantListTitle } from "@/react/components/Spaces/ChatSpace/RailBar/ParticipantListTitle";

import { ChatRoomDescription } from "./ChatRoomDescription";
import { mapParticipantsPerStatus, sortParticipants } from "./helpers";
import { ParticipantListComponent } from "./ParticipantList/ParticipantListComponent";
import { useParticipantActions } from "./useParticipantActions";

export interface MemberListProps {
  chatRoom: any;
  description: string;
  refetchChatRoom: () => void;
}

export const MemberList = ({
  chatRoom,
  refetchChatRoom,
  description,
}: MemberListProps) => {
  const showMemberProfile = useOpenMemberProfile();
  const { uuid, current_participant: currentParticipant } = chatRoom;
  const {
    chatParticipants = [],
    refetchChatParticipants,
    totalCount: totalParticipantsCount,
    hasNextPage,
    fetchNextPage,
  } = useChatParticipantsApi(uuid, { shouldDisableCache: true, per_page: 25 });

  const participants = [...sortParticipants(chatParticipants)];
  const { handleMakeAdmin, removeParticipant } = useParticipantActions({
    refetchChatParticipants,
    refetchChatRoom,
    uuid,
  });

  const onSuccessBlockMember = () => {
    refetchChatParticipants();
  };

  const {
    isBlocking,
    memberToBlock,
    toggleBlockDmsModal,
    handleBlockMember,
    handleUnblockMember,
    isBlockDmsModalOpen,
    setIsBlockDmsModalOpen,
  } = useBlockMember({
    onSuccess: onSuccessBlockMember,
  });

  const { onlineParticipants, offlineParticipants } =
    mapParticipantsPerStatus(participants);

  return (
    <>
      <ChatRoomDescription description={description} />
      <div
        id="scrollableDiv"
        className={classNames("h-full overflow-auto pt-2")}
      >
        <InfiniteScroll
          scrollThreshold={0.8}
          next={fetchNextPage}
          hasMore={Boolean(hasNextPage)}
          dataLength={totalParticipantsCount}
          className="px-6 pb-6"
          scrollableTarget="scrollableDiv"
          loader={<Loader center />}
        >
          {onlineParticipants.length > 0 && (
            <>
              <ParticipantListTitle title={t("chat_space.online")} />
              <ParticipantListComponent
                currentParticipant={currentParticipant}
                toggleBlockDmsModal={toggleBlockDmsModal}
                handleMakeAdmin={handleMakeAdmin}
                onRemoveParticipant={removeParticipant}
                onUnblockMemberDm={handleUnblockMember}
                participantList={onlineParticipants}
                showMemberProfile={showMemberProfile}
              />
            </>
          )}
          {offlineParticipants.length > 0 && (
            <>
              <ParticipantListTitle title={t("chat_space.offline")} />
              <ParticipantListComponent
                currentParticipant={currentParticipant}
                toggleBlockDmsModal={toggleBlockDmsModal}
                handleMakeAdmin={handleMakeAdmin}
                onRemoveParticipant={removeParticipant}
                onUnblockMemberDm={handleUnblockMember}
                participantList={offlineParticipants}
                showMemberProfile={showMemberProfile}
              />
            </>
          )}
        </InfiniteScroll>
      </div>
      <BlockDmsModal
        showModal={isBlockDmsModalOpen}
        onClose={() => setIsBlockDmsModalOpen(false)}
        onConfirm={handleBlockMember}
        memberName={memberToBlock?.name || ""}
        isLoading={isBlocking}
      />
    </>
  );
};
